import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@env/environment';
import { ShelfService } from '@app-services/shelf.service';
import { SessionStore, UserInfo } from '@app/store/session/session.store';
import {
  PURCHASE_PROFILES,
  buildPurchaseProfileQuestions,
  DealerCityInfo,
  DealerPurchaseProfileRequest,
  DealerPurchaseProfileResponse,
  DEFAULT_PURCHASE_PROFILE_ANSWERS,
  DEFAULT_PURCHASE_PROFILE_OPTIONS,
  FipeAverageResponse,
  ProfileTypeId,
  ProfileTypeInfo,
  PurchaseProfileAnswers,
  PurchaseProfileOnboarding,
  PurchaseProfileOptions,
  PurchaseProfileVehicleFilter,
  PurchaseProfileDefinition,
  PurchaseProfileQuestion,
} from './purchase-profile.model';

@Injectable({ providedIn: 'root' })
export class PurchaseProfileService {
  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  private readonly shelfService = inject(ShelfService);
  private readonly sessionStore = inject(SessionStore);

  private readonly baseUrl = `${environment.api}/dealer/purchase-profile`;
  private fromOnboardingPrompt = false;
  private savedDuringVisit = false;

  readonly answers = signal<PurchaseProfileAnswers>({ ...DEFAULT_PURCHASE_PROFILE_ANSWERS });
  readonly profileType = signal<ProfileTypeInfo | null>(null);
  readonly savedAt = signal<string | null>(null);
  readonly suggested = signal<boolean>(false);
  readonly options = signal<PurchaseProfileOptions>({ ...DEFAULT_PURCHASE_PROFILE_OPTIONS });
  readonly dealerCity = signal<DealerCityInfo | null>(null);
  readonly dealerUf = signal<string | null>(null);
  readonly onboarding = signal<PurchaseProfileOnboarding | null>(null);
  readonly vehicleFilter = signal<PurchaseProfileVehicleFilter | null>(null);

  readonly fipeAverage = signal<number | null>(null);

  readonly isModalOpen = signal<boolean>(false);
  readonly currentStep = signal<number>(0);
  readonly isSaving = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);

  readonly isCompleted = computed<boolean>(() => {
    return this.onboarding()?.status === 'COMPLETED' || !!this.savedAt();
  });

  readonly pending = computed<boolean>(() => !this.isCompleted());

  readonly currentProfile = computed<PurchaseProfileDefinition | null>(() => {
    const pt = this.profileType();
    if (!pt?.id) return null;
    return PURCHASE_PROFILES[pt.id as ProfileTypeId] || null;
  });

  readonly showBanner = computed<boolean>(() => {
    const onb = this.onboarding();
    return !this.isCompleted() && !this.isModalOpen() && !!onb;
  });

  readonly showMenuEntry = computed<boolean>(() => !this.isLoading() && !!this.onboarding());

  readonly showPendingBadge = computed<boolean>(() => this.showMenuEntry() && this.pending());

  readonly menuHint = computed<string>(() => {
    const pt = this.profileType();
    if (this.isCompleted() && pt?.name) {
      return `${pt.name} · refazer`;
    }
    return 'Descubra em 5 perguntas';
  });

  readonly fipeAverageFormatted = computed<string | null>(() => {
    const avg = this.fipeAverage();
    if (avg === null || avg === undefined || Number.isNaN(avg)) {
      return null;
    }
    return `${Math.round(avg * 100)}%`;
  });

  readonly questions = computed<PurchaseProfileQuestion[]>(() => {
    return buildPurchaseProfileQuestions(this.options(), {
      dealerCity: this.dealerCity() || undefined,
      dealerUf: this.dealerUf() || undefined,
    });
  });

  constructor() {
    this.loadProfile().subscribe();
    this.loadFipeAverage().subscribe();
  }

  adoptOnboarding(onboarding: PurchaseProfileOnboarding | undefined): void {
    if (!onboarding || this.onboarding()) {
      return;
    }
    this.onboarding.set(onboarding);
  }

  openModal(step?: number, options?: { fromOnboarding?: boolean }): void {
    this.fromOnboardingPrompt = !!options?.fromOnboarding;
    this.savedDuringVisit = false;
    if (typeof step === 'number') {
      this.currentStep.set(step);
    } else {
      this.currentStep.set(this.isCompleted() ? 1 : 0);
    }
    this.isModalOpen.set(true);
  }

  shouldRegisterSkip(): boolean {
    if (this.savedDuringVisit) {
      return false;
    }
    if (!this.isCompleted()) {
      return true;
    }
    return this.fromOnboardingPrompt;
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.currentStep.set(0);
  }

  setStep(step: number): void {
    this.currentStep.set(step);
  }

  loadProfile(): Observable<DealerPurchaseProfileResponse | null> {
    this.isLoading.set(true);
    return this.http.get<DealerPurchaseProfileResponse>(this.baseUrl).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res) {
          this.applyResponse(res);
        }
      }),
      catchError(() => {
        this.isLoading.set(false);
        return of(null);
      })
    );
  }

  loadFipeAverage(): Observable<number | null> {
    return this.http.get<FipeAverageResponse>(`${this.baseUrl}/fipe-average`).pipe(
      map((res) => res?.average ?? null),
      tap((avg) => {
        if (typeof avg === 'number') {
          this.fipeAverage.set(avg);
        }
      }),
      catchError(() => of(null))
    );
  }

  saveProfile(request: DealerPurchaseProfileRequest): Observable<DealerPurchaseProfileResponse | null> {
    this.isSaving.set(true);
    return this.http.put<DealerPurchaseProfileResponse>(this.baseUrl, request).pipe(
      tap((res) => {
        this.isSaving.set(false);
        if (res) {
          this.savedDuringVisit = true;
          this.applyResponse(res);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this.isSaving.set(false);
        const body = err.error as { message?: string } | null;
        const backendMessage = body?.message;
        const msg =
          typeof backendMessage === 'string' && backendMessage.trim().length > 0
            ? backendMessage
            : 'Não foi possível salvar seu perfil agora. Tente novamente.';
        this.toastr.error(msg);
        return of(null);
      })
    );
  }

  skipProfile(): Observable<PurchaseProfileOnboarding | null> {
    return this.http.post<PurchaseProfileOnboarding>(`${this.baseUrl}/skip`, {}).pipe(
      tap((onb) => {
        if (onb) {
          this.onboarding.set(onb);
          this.syncOnboarding(onb);
        }
      }),
      catchError(() => of(null))
    );
  }

  notifyProfileSaved(): void {
    this.toastr.success('Perfil de compra salvo com sucesso');
  }

  reloadForSelectedDealer(): Observable<DealerPurchaseProfileResponse | null> {
    this.closeModal();
    this.shelfService.clearShelfCache();
    this.resetProfileState();
    this.syncOnboarding(undefined);
    return this.loadProfile();
  }

  private applyResponse(res: DealerPurchaseProfileResponse): void {
    if (res.answers) {
      this.answers.set({
        vehicleTypes: res.answers.vehicleTypes || [],
        priceRanges: res.answers.priceRanges || [],
        fipeDiscountRanges: res.answers.fipeDiscountRanges || [],
        region: res.answers.region,
        vehicleCondition: res.answers.vehicleCondition,
      });
    }

    if (res.profileType) {
      this.profileType.set(res.profileType);
    }
    this.savedAt.set(res.savedAt || null);
    this.suggested.set(!!res.suggested);

    if (res.options) {
      this.options.set(res.options);
    }
    if (res.dealerCity) {
      this.dealerCity.set(res.dealerCity);
    }
    if (res.dealerUf) {
      this.dealerUf.set(res.dealerUf);
    }
    if (res.onboarding) {
      this.onboarding.set(res.onboarding);
      this.syncOnboarding(res.onboarding);
    }
    this.vehicleFilter.set(res.vehicleFilter ?? null);
  }

  private resetProfileState(): void {
    this.answers.set({ ...DEFAULT_PURCHASE_PROFILE_ANSWERS });
    this.profileType.set(null);
    this.savedAt.set(null);
    this.suggested.set(false);
    this.dealerCity.set(null);
    this.dealerUf.set(null);
    this.onboarding.set(null);
    this.vehicleFilter.set(null);
  }

  private syncOnboarding(onboarding: PurchaseProfileOnboarding | undefined): void {
    this.sessionStore.update((state) => {
      if (!state.userInfo) {
        return state;
      }
      const userInfo = Object.assign(new UserInfo(), state.userInfo);
      userInfo.purchaseProfileOnboarding = onboarding;
      return { ...state, userInfo };
    });
  }
}
